import React, { useState, useRef, useEffect } from 'react';
import '../styles/MusicGenerator.css';
import { FaMusic, FaPlay, FaPause, FaSpinner, FaDownload } from 'react-icons/fa';

const API_KEY = 'CdWxiwLHaHIF69BgdJnvmhI2JwMMeXCYBi4Kr0TEFxyHw5J5bNbTX6lzl0yD';
const API_URL = 'https://modelslab.com/api/v6/voice/music_gen';

const musicStyles = [
  "Electronic dance music with heavy bass and synth leads, 128 bpm",
  "Ambient piano melody with soft pads and atmospheric textures, 80 bpm",
  "Hip hop beat with trap drums and melodic samples, 90 bpm",
  "Jazz fusion with complex chord progressions and smooth saxophone, 96 bpm",
  "Rock instrumental with distorted guitars and dynamic drums, 120 bpm",
  "Latin groove with percussion, brass section and salsa rhythm, 98 bpm",
  "Orchestral cinematic score with strings and epic drums, 110 bpm",
  "Lo-fi beats with vinyl crackle and mellow piano, 85 bpm",
  "Dubstep with heavy wobble bass and glitch effects, 140 bpm",
  "Tropical house with marimba and uplifting melody, 124 bpm"
];

const MusicGenerator = () => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [musicUrl, setMusicUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [futureLink, setFutureLink] = useState(null);
  const audioRef = useRef(null);
  const pollingRef = useRef(null);

  useEffect(() => {
    // Set a random music style as placeholder
    const randomStyle = musicStyles[Math.floor(Math.random() * musicStyles.length)];
    setPrompt(randomStyle);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const checkAudioAvailability = async (url) => {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch (err) {
      return false;
    }
  };

  const fetchResult = async (fetchUrl) => {
    try {
      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          key: API_KEY
        })
      });
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      
      console.log('Fetch Result:', data);
      
      if (data.status === 'success' && data.output && data.output[0]) {
        setMusicUrl(data.output[0]);
        setProcessingStatus(null);
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      } else if (data.status === 'processing') {
        setProcessingStatus({
          ...data,
          message: 'Creating your unique music track...'
        });
        if (data.future_links && data.future_links[0] && !futureLink) {
          setFutureLink(data.future_links[0]);
        }
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Error fetching result:', err);
      if (futureLink) {
        const isAvailable = await checkAudioAvailability(futureLink);
        if (isAvailable) {
          setMusicUrl(futureLink);
          setProcessingStatus(null);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          return;
        }
      }
      setError('Something went wrong. Please try a different style.');
    }
  };

  const startPolling = (fetchUrl) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    pollingRef.current = setInterval(() => {
      fetchResult(fetchUrl);
    }, 2000);

    if (futureLink) {
      const checkFutureLink = setInterval(async () => {
        const isAvailable = await checkAudioAvailability(futureLink);
        if (isAvailable) {
          setMusicUrl(futureLink);
          setProcessingStatus(null);
          clearInterval(checkFutureLink);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        }
      }, 2000);
    }
  };

  const generateMusic = async () => {
    if (!prompt.trim()) {
      setError('Please enter a music description');
      return;
    }

    setLoading(true);
    setError('');
    setMusicUrl(null);
    setProcessingStatus(null);
    setFutureLink(null);

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          key: API_KEY,
          prompt: prompt,
          init_audio: "https://pub-3626123a908346a7a8be8d9295f44e26.r2.dev/livewire-tmp/IxizU51vL2IwiEjy6XtvbDECa9f16E-metaU2hhcGUgb2YgeW91IFJpbmd0b25lIO+9nO+9nCBJbnN0cnVtZW50YWwgQkdNIO+9nO+9nCBMSUdIVCBHT0pPLndhdg==-.wav",
          sampling_rate: 32000,
          base64: false,
          temp: false
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `API Error: ${response.status}`);
      }

      const data = await response.json();
      console.log('Music Generation Response:', data);

      if (data.status === 'processing') {
        setProcessingStatus({
          ...data,
          message: 'Creating your unique music track...'
        });
        if (data.future_links && data.future_links[0]) {
          setFutureLink(data.future_links[0]);
        }
        if (data.fetch_result) {
          startPolling(data.fetch_result);
        }
      } else if (data.status === 'success' && data.output && data.output[0]) {
        setMusicUrl(data.output[0]);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Music Generation Error:', err);
      setError('Something went wrong. Please try a different style.');
    } finally {
      setLoading(false);
    }
  };

  const getRandomStyle = () => {
    const randomStyle = musicStyles[Math.floor(Math.random() * musicStyles.length)];
    setPrompt(randomStyle);
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  return (
    <div className="music-generator">
      <div className="generator-header">
        <FaMusic className="header-icon" />
        <h2>AI Music Generator</h2>
        <p>Create unique music with AI</p>
      </div>

      <div className="generator-content">
        <div className="input-section">
          <textarea
            placeholder="Describe your music style, instruments, tempo, and mood..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={loading || processingStatus}
          />
          <button 
            onClick={getRandomStyle}
            className="random-style-button"
            disabled={loading || processingStatus}
          >
            Try Different Style
          </button>
        </div>

        <button 
          onClick={generateMusic}
          disabled={loading || !prompt.trim() || processingStatus}
          className="generate-button"
        >
          {loading || processingStatus ? (
            <>
              <FaSpinner className="spinner" />
              {processingStatus ? 'Creating Music...' : 'Starting Generation...'}
            </>
          ) : 'Generate Music'}
        </button>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {processingStatus && (
          <div className="processing-message">
            Creating your unique music track...
            {futureLink && <div className="future-link-status">Almost ready...</div>}
          </div>
        )}

        {musicUrl && (
          <div className="result-section">
            <div className="audio-player">
              <audio
                ref={audioRef}
                src={musicUrl}
                onEnded={handleAudioEnded}
              />
              <button 
                className="play-button"
                onClick={togglePlay}
              >
                {isPlaying ? <FaPause /> : <FaPlay />}
              </button>
              <a 
                href={musicUrl}
                download="ai-generated-music.wav"
                className="download-button"
              >
                <FaDownload />
                <span>Download</span>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MusicGenerator; 